/**
 * The monetization funnel, as one Firestore collection.
 *
 * GA4 gets the same events it always did, but without a user id it cannot say
 * how many distinct people reached each stage, and the per-stage counters in
 * shop_stats were admin-write-only so they only ever counted our own sessions.
 * `monetization_events` records one row per stage per person, tagged with
 * what the admin funnel panel needs to segment by: guest or account, tier,
 * how much they have played, and how big the room was at the time.
 *
 * Stages, in funnel order:
 *   exposure       a monetization surface was on screen (trial banner, locked tile, sale strip)
 *   membership_cta a membership call-to-action was clicked
 *   shop_link      a link into the shop was clicked from inside play (packs, effects, deluxe)
 *   shop_view      the shop opened on a category
 *   item_preview   an item's preview/details opened
 *   demo_start     a free demo round of a purchase-only game was opened
 *   buy_click      a Buy / Try Free / Unlock button was pressed
 *   guest_gate     a guest hit the sign-in wall on a purchase path
 *   consent_decline the mature-content warning was cancelled
 *   notification_click   a notification-center action was pressed
 *   notification_dismiss the notification's explicit X/dismiss button was pressed
 *   checkout_open  a Paddle transaction was created for the caller (server)
 *   purchase       money or currency changed hands (server)
 */
import { addDoc, collection, serverTimestamp, type Firestore } from 'firebase/firestore';
import { isPlaytestBotUid, isSurfacePreviewUid } from './synthetic-players';
import type { User } from 'firebase/auth';
import { getDeviceCategory } from '@/hooks/use-device-category';
import { hasActiveAdFreeMembership, type AdViewerSegment } from './ad-access';
import { getSessionTrafficSource, type TrafficSource } from './traffic-source';
import type { DisplayAdArm } from './display-ad-provider';
import { noteAdExperiment } from './ad-experiment-session';

export const MONETIZATION_EVENTS_COLLECTION = 'monetization_events';

export const MONETIZATION_STAGES = [
  'exposure',
  'membership_cta',
  'shop_link',
  'shop_view',
  'item_preview',
  // The free round of a purchase-only game (lib/deluxe-game-demo.ts). Not a
  // buy_click: nothing is bought and no checkout follows. Without it a host
  // who played the demo and one who never pressed anything looked the same.
  'demo_start',
  'buy_click',
  'guest_gate',
  // The mature-content warning was cancelled, on a demo or on an unlock. Two
  // of the three Deluxe games carry the warning, so without this "declined
  // the warning" and "never pressed" could not be told apart.
  'consent_decline',
  // A free trial start. Deliberately NOT 'purchase': nobody is charged, there
  // is no card, and counting it as one put a $0 "money purchase" and a
  // "membership buyer" in the funnel while revenue stayed at $0.00.
  'trial_start',
  // A free trial ending, whichever way it ended. Without this the funnel had
  // no denominator: trial_start was recorded and then nothing, so the only way
  // to count how many trials lapsed rather than converted was to scan every
  // user document for membershipTrialExpiredAt. The surface says which ending
  // it was, 'trial_lapse' or 'trial_cancel'.
  'trial_end',
  // A free in-product reward was claimed - today only the post-match Supply
  // Drop (src/lib/supply-drop.ts). Deliberately NOT 'buy_click': nothing is
  // bought, nothing is spent and no checkout can follow, so counting it as a
  // buy would put purchase intent in the funnel that does not exist. It is a
  // stage of its own for the same reason trial_start stopped being a purchase.
  // It still pairs against 'exposure' in the per-surface shown/clicked panel,
  // because "we showed a crate and nobody opened it" is worth seeing.
  'reward_claim',
  // Notification-center engagement is reported in its own Admin Funnel table.
  // These are not purchase-conversion steps, but using the same event stream
  // keeps the selected 7/30/90-day window and account segmentation consistent.
  'notification_click',
  'notification_dismiss',
  'checkout_open',
  'purchase',
] as const;

export type MonetizationStage = (typeof MONETIZATION_STAGES)[number];
export type MonetizationTier = 'guest' | 'registered' | 'lite' | 'elite';

export interface MonetizationEventInput {
  stage: MonetizationStage;
  /** Where it happened, e.g. 'shop', 'home_trial_banner', 'deluxe_gate', 'word_pack_selector'. */
  surface: string;
  /**
   * Which button on that surface, when it has more than one (the shop has a
   * trial button on its plan card, its phone card, its item card...). Shown in
   * the admin funnel's "Latest presses" list. Since 2026-10-02.
   */
  placement?: string | null;
  itemId?: string | null;
  category?: string | null;
  lobbyId?: string | null;
  lobbySize?: number | null;
  /** 'credits' | 'nanoCores' | 'usd' for buy/purchase stages. */
  currency?: string | null;
  /** Real-money amount in USD for purchases, or the credit/core price. */
  amount?: number | null;
}

export interface MonetizationSubject {
  uid: string;
  isGuest: boolean;
  tier: MonetizationTier;
  roundBucket: string;
  totalRounds: number;
  /** Added with the ad-policy rollout; historical rows may not have it. */
  adSegment?: AdViewerSegment;
}

export interface MonetizationEventDoc extends MonetizationEventInput, MonetizationSubject {
  device: string;
  path: string | null;
  tsMs: number;
  /**
   * The category of the visit this row came from (lib/traffic-source.ts).
   * Client rows only, and only since 2026-09-24; server rows never carry it.
   */
  entrySource?: TrafficSource;
  /**
   * The viewer's TinyAdz experiment arm (lib/display-ad-provider.ts). Client
   * rows only, since 2026-09-26; server rows can recompute it from the uid with
   * displayAdBucket().
   */
  adArm?: DisplayAdArm;
}

let currentAdArm: DisplayAdArm | null = null;

/** Set by AdExperimentTracker once the arm is known; stamped on later client rows. */
export function setMonetizationAdArm(arm: DisplayAdArm | null) {
  currentAdArm = arm;
}

/** Same buckets analytics.ts uses on game events, so the two datasets line up. */
export function roundBucket(totalRounds: number | undefined | null): string {
  if (totalRounds === undefined || totalRounds === null) return 'unknown';
  if (totalRounds === 0) return 'new';
  if (totalRounds < 5) return 'first_steps';
  if (totalRounds < 20) return 'casual';
  if (totalRounds < 50) return 'regular';
  if (totalRounds < 150) return 'veteran';
  return 'elite';
}

export function subjectFromProfile(
  uid: string,
  isAnonymous: boolean,
  profile: {
    isPremium?: boolean;
    isLite?: boolean;
    subscriptionStatus?: string;
    subscriptionCancelAtPeriodEnd?: boolean;
    subscriptionCancellationEffectiveAt?: unknown;
    subscriptionTrialEndsAt?: unknown;
    membershipTrialEndsAt?: unknown;
    premiumUntil?: unknown;
    adAccessCohort?: 'legacy_ad_free' | 'new_free';
    legacyAdFree?: boolean;
    progression?: { totalRounds?: number };
  } | null | undefined,
): MonetizationSubject {
  const totalRounds = Number(profile?.progression?.totalRounds) || 0;
  const tier: MonetizationTier = isAnonymous
    ? 'guest'
    : profile?.isPremium
      ? 'elite'
      : profile?.isLite
        ? 'lite'
        : 'registered';
  const adSegment: AdViewerSegment = isAnonymous
    ? 'guest'
    : hasActiveAdFreeMembership(profile as any)
      ? 'member'
      : profile?.adAccessCohort === 'new_free'
        ? 'new_free'
        : 'legacy';
  return { uid, isGuest: isAnonymous, tier, adSegment, roundBucket: roundBucket(totalRounds), totalRounds };
}

export function buildMonetizationEvent(
  subject: MonetizationSubject,
  input: MonetizationEventInput,
  context: { device: string; path: string | null; nowMs: number; entrySource?: TrafficSource | null },
): MonetizationEventDoc {
  const doc: MonetizationEventDoc = {
    ...subject,
    stage: input.stage,
    surface: input.surface.slice(0, 60),
    device: context.device,
    path: context.path,
    tsMs: context.nowMs,
  };
  if (context.entrySource) doc.entrySource = context.entrySource;
  if (input.placement) doc.placement = String(input.placement).slice(0, 60);
  if (input.itemId) doc.itemId = String(input.itemId).slice(0, 80);
  if (input.category) doc.category = String(input.category).slice(0, 40);
  if (input.lobbyId) doc.lobbyId = String(input.lobbyId).slice(0, 40);
  if (typeof input.lobbySize === 'number' && Number.isFinite(input.lobbySize)) doc.lobbySize = Math.max(0, Math.trunc(input.lobbySize));
  if (input.currency) doc.currency = String(input.currency).slice(0, 12);
  if (typeof input.amount === 'number' && Number.isFinite(input.amount)) doc.amount = input.amount;
  return doc;
}

const sessionSeen = new Set<string>();

/** Some stages are impressions; one row per tab session per key is enough. */
export function dedupeKeyFor(input: MonetizationEventInput): string | null {
  if (input.stage === 'shop_view') return `shop_view:${input.category || 'all'}`;
  if (input.stage === 'exposure') return `exposure:${input.surface}:${input.itemId || ''}`;
  return null;
}

/**
 * Best-effort client write. Never throws, never blocks: a failed analytics row
 * must not stop a purchase or a page.
 */
export function logMonetizationEvent(
  firestore: Firestore | null | undefined,
  user: Pick<User, 'uid' | 'isAnonymous'> | null | undefined,
  profile: Parameters<typeof subjectFromProfile>[2],
  input: MonetizationEventInput,
): void {
  if (!firestore || !user) return;
  // Bot playtests sign in as real accounts; their impressions are not a funnel.
  // Neither are the admin surface preview's made-up viewers.
  if (isPlaytestBotUid(user.uid) || isSurfacePreviewUid(user.uid)) return;
  const key = dedupeKeyFor(input);
  if (key) {
    if (sessionSeen.has(key)) return;
    sessionSeen.add(key);
  }
  try {
    const doc = buildMonetizationEvent(subjectFromProfile(user.uid, !!user.isAnonymous, profile), input, {
      device: getDeviceCategory(),
      path: typeof window !== 'undefined' ? window.location.pathname : null,
      nowMs: Date.now(),
      entrySource: getSessionTrafficSource(),
    });
    if (currentAdArm) doc.adArm = currentAdArm;
    if (input.stage === 'shop_view') noteAdExperiment('shop_views');
    if (input.stage === 'buy_click') noteAdExperiment('buy_clicks');
    addDoc(collection(firestore, MONETIZATION_EVENTS_COLLECTION), { ...doc, ts: serverTimestamp() }).catch((error) => {
      if (process.env.NODE_ENV === 'development') console.warn('[monetization] event write failed', error);
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.warn('[monetization] event build failed', error);
  }
}

/** Test hook: forget impression dedupe between test cases. */
export function resetMonetizationDedupe() {
  sessionSeen.clear();
}
